<!-- Loading State -->
@if (loading) {
  <div class="container-xxl py-20">
    <div class="text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-5">Loading event details...</p>
    </div>
  </div>
}

<!-- Event Details -->
@if (!loading && event) {
  <div class="event-details-page container" style="max-width: 1400px">
    <!-- Event Header Banner -->
    <div class="event-header" [style.background-image]="event.header_url ? 'url(' + event.header_url + ')' : 'none'">
      <div class="event-header-overlay">
        <div class="container-xxl" style="max-width: 1400px;">
          <div class="event-header-content">
            <h1 class="event-title" style="color: white">{{ event.event_name }}</h1>
            <div class="event-meta">
              @if (event.start_date) {
                <span class="event-meta-item">
                  <i class="fa fa-calendar me-2"></i>
                  {{ formatDateDMYDots(event.start_date) }}
                  @if (event.end_date && event.start_date !== event.end_date) {
                    - {{ formatDateDMYDots(event.end_date) }}
                  }
                </span>
              }
              <span class="event-meta-item">
                <i class="fa fa-location-dot me-2"></i>
                {{ event.city }}, {{ event.country }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container-xxl py-10" style="max-width: 1400px;">
      <div class="row g-8">
        <!-- Main Content -->
        <div class="col-lg-8">
          <!-- Event Description -->
          <div class="card mb-8">
            <div class="card-body p-8">
              <h2 class="card-title fw-bold mb-5">About This Event</h2>
              <div class="event-description-content" [innerHTML]="event.event_description"></div>
            </div>
          </div>

          <!-- Event Classes -->
          <div class="card mb-8">
            <div class="card-header">
              <h3 class="card-title fw-bold m-0">
                <i class="fa fa-list text-primary me-3"></i>Available Classes
              </h3>
            </div>
            <div class="card-body p-0">
              @if (loadingClasses) {
                <div class="text-center py-10">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>
              } @else if (eventClasses.length > 0) {
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead>
                      <tr class="fw-bold text-muted bg-light">
                        <th class="ps-8">Class Name</th>
                        <th>Start Time</th>
                        <th>Registrations</th>
                        <th class="pe-8">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (eventClass of eventClasses; track eventClass.event_class_id) {
                        <tr>
                          <td class="ps-8">
                            <div class="fw-semibold">{{ eventClass.class_name }}</div>
                            @if (eventClass.class_description) {
                              <div class="text-muted fs-7">{{ eventClass.class_description }}</div>
                            }
                          </td>
                          <td>
                            @if (eventClass.start_date_time) {
                              <span>{{ formatDateTime(eventClass.start_date_time) }}</span>
                            } @else {
                              <span class="text-muted">TBA</span>
                            }
                          </td>
                          <td>
                            <span>{{ getClassRegistrationCount(eventClass) }}</span>
                          </td>
                          <td class="pe-8">
                            @if (eventClass.price) {
                              <span class="fw-semibold">{{ eventClass.price }} {{ eventClass.price_currency || 'kr' }}</span>
                            } @else {
                              <span class="text-muted">Free</span>
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              } @else {
                <div class="text-center py-10 text-muted">
                  <i class="fa fa-list fs-3x mb-5"></i>
                  <p>No classes available for this event</p>
                </div>
              }
            </div>
          </div>

          <!-- Event Services -->
          @if (eventServices.length > 0) {
            <div class="card mb-8">
              <div class="card-header">
                <h3 class="card-title fw-bold m-0">
                  <i class="fa fa-concierge-bell text-success me-3"></i>Available Services
                </h3>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead>
                      <tr class="fw-bold text-muted bg-light">
                        <th class="ps-8">Service Name</th>
                        <th>Available</th>
                        <th class="pe-8">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (service of eventServices; track service.event_service_id) {
                        <tr>
                          <td class="ps-8">
                            <div class="fw-semibold">{{ service.event_service_name }}</div>
                            @if (service.event_service_description) {
                              <div class="text-muted fs-7">{{ service.event_service_description }}</div>
                            }
                          </td>
                          <td>
                            @if (service.max_num_registrations) {
                              <span>{{ getServiceAvailableSlots(service) }}/{{ service.max_num_registrations }}</span>
                            } @else {
                              <span class="text-muted">Unlimited</span>
                            }
                          </td>
                          <td class="pe-8">
                            @if (service.price) {
                              <span class="fw-semibold">{{ service.price }} {{ service.price_currency || 'kr' }}</span>
                            } @else {
                              <span class="text-muted">Free</span>
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }

        </div>

        <!-- Sidebar -->
        <div class="col-lg-4">
          <!-- Registration Card -->
          <div class="card mb-8 sticky-top" style="top: 20px;">
            <div class="card-body p-8">
              <h3 class="fw-bold mb-6">Event Registration</h3>

              <!-- Registration Deadline -->
              @if (event.registration_deadline) {
                <div class="alert alert-warning d-flex align-items-center mb-6">
                  <i class="fa fa-clock fs-2x me-3"></i>
                  <div>
                    <div class="fw-semibold">Registration Deadline</div>
                    <div class="fs-7">{{ formatDateDMYDots(event.registration_deadline) }}</div>
                  </div>
                </div>
              }

              <!-- Register Button -->
              <app-button
                [variant]="'primary'"
                [label]="'Register to Event'"
                [iconClass]="'fa fa-check-circle'"
                class="w-100 mb-6"
                (clicked)="registerToEvent()"
              />

              <div class="separator my-6"></div>

              <!-- Event Info -->
              <div class="event-info-list">
                <div class="event-info-item mb-5">
                  <div class="event-info-label">
                    <i class="fa fa-calendar text-primary me-2"></i>
                    <span class="fw-semibold">Dates</span>
                  </div>
                  <div class="event-info-value">
                    {{ formatDateDMYDots(event.start_date) }}
                    @if (event.end_date && event.start_date !== event.end_date) {
                      <br>to {{ formatDateDMYDots(event.end_date) }}
                    }
                  </div>
                </div>

                <div class="event-info-item mb-5">
                  <div class="event-info-label">
                    <i class="fa fa-location-dot text-primary me-2"></i>
                    <span class="fw-semibold">Location</span>
                  </div>
                  <div class="event-info-value">
                    <div>{{ event.city }}, {{ event.country }}</div>
                    @if (event.address) {
                      <a [href]="getGoogleMapsUrl(event)" target="_blank" class="text-primary text-decoration-none fs-7">
                        {{ event.address }}
                        <i class="fa fa-external-link ms-1"></i>
                      </a>
                    }
                  </div>
                </div>

                <div class="event-info-item">
                  <div class="event-info-label">
                    <i class="fa fa-list text-primary me-2"></i>
                    <span class="fw-semibold">Classes</span>
                  </div>
                  <div class="event-info-value">
                    {{ eventClasses.length }} classes available
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Event Contacts -->
          @if (eventContacts.length > 0) {
            <div class="card">
              <div class="card-body p-8">
                <h3 class="fw-bold mb-6">Event Contacts</h3>

                @if (loadingContacts) {
                  <div class="text-center py-5">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                  </div>
                } @else {
                  <div class="d-flex flex-column gap-4">
                    @for (contact of eventContacts; track contact.contact_id) {
                      <div class="contact-card">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                          <div class="flex-grow-1">
                            <h4 class="mb-1 fs-5 fw-semibold">{{ contact.first_name }} {{ contact.last_name }}</h4>
                            <span class="badge badge-light-primary">{{ contact.role?.role_name || 'N/A' }}</span>
                          </div>
                        </div>

                        <div class="separator mb-3"></div>

                        <div class="d-flex flex-column gap-2">
                          @if (contact.email) {
                            <div class="d-flex align-items-center">
                              <i class="fa fa-envelope text-muted me-2"></i>
                              <a [href]="'mailto:' + contact.email" class="text-gray-700 text-decoration-none">{{ contact.email }}</a>
                            </div>
                          }
                          @if (contact.phone) {
                            <div class="d-flex align-items-center">
                              <i class="fa fa-phone text-muted me-2"></i>
                              <a [href]="'tel:' + contact.phone" class="text-gray-700 text-decoration-none">{{ contact.phone }}</a>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Sponsors Section -->
    @if (eventSponsors.length > 0) {
      <div class="sponsors-section">
        <h3>Proudly Sponsored By</h3>
        <div class="sponsors-marquee-container">
          <div class="sponsors-marquee">
            <div class="sponsors-track" [class.animate]="shouldAnimateSponsors()" [class.static]="!shouldAnimateSponsors()">
              @for (eventSponsor of eventSponsors; track eventSponsor.entry_id) {
                @if (eventSponsor.sponsor) {
                  <div class="sponsor-slide">
                    <div class="sponsor-card-animated">
                      <div class="sponsor-badge">
                        @if (eventSponsor.sponsor.logo_url) {
                          <img [src]="eventSponsor.sponsor.logo_url"
                               [alt]="eventSponsor.sponsor.sponsor_name"
                               class="sponsor-logo-animated" />
                        }
                        <div class="sponsor-shine"></div>
                      </div>
                      <div class="sponsor-details">
                        <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                        @if (eventSponsor.sponsor.sponsor_description) {
                          <p>{{ eventSponsor.sponsor.sponsor_description }}</p>
                        }
                      </div>
                    </div>
                  </div>
                }
              }
              <!-- Duplicate for seamless loop (only when animating) -->
              @if (shouldAnimateSponsors()) {
                @for (eventSponsor of eventSponsors; track eventSponsor.entry_id) {
                  @if (eventSponsor.sponsor) {
                    <div class="sponsor-slide">
                      <div class="sponsor-card-animated">
                        <div class="sponsor-badge">
                          @if (eventSponsor.sponsor.logo_url) {
                            <img [src]="eventSponsor.sponsor.logo_url"
                                 [alt]="eventSponsor.sponsor.sponsor_name"
                                 class="sponsor-logo-animated" />
                          }
                          <div class="sponsor-shine"></div>
                        </div>
                        <div class="sponsor-details">
                          <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                          @if (eventSponsor.sponsor.sponsor_description) {
                            <p>{{ eventSponsor.sponsor.sponsor_description }}</p>
                          }
                        </div>
                      </div>
                    </div>
                  }
                }
              }
            </div>
          </div>
        </div>
      </div>
    }
  </div>
}

<!-- Event Not Found -->
@if (!loading && !event) {
  <div class="container-xxl py-20">
    <div class="text-center">
      <i class="fa fa-calendar-xmark fs-3x text-muted mb-5"></i>
      <h3 class="text-muted">Event Not Found</h3>
      <p class="text-muted">The event you're looking for doesn't exist or has been removed.</p>
      <app-button
        [variant]="'primary'"
        [label]="'Back to Events'"
        [routerLink]="'/shows'"
      />
    </div>
  </div>
}
